import React from 'react';
import { Segment } from '@heroui-pro/react';
import { useTranslation } from 'react-i18next';

type DashboardLanguage = 'ka' | 'en';

type DashboardLanguageToggleProps = {
    className?: string;
    id: string;
    itemIdPrefix: string;
};

const languageStorageKey = 'camus-language';
const languageChangeEvent = 'camus-language-change';

const dashboardLanguages = [
    'ka',
    'en',
] as const satisfies readonly DashboardLanguage[];
const languageItems = [
    {
        label: 'ქარ',
        labelKey: 'layout.language.georgian',
        language: 'ka',
    },
    {
        label: 'Eng',
        labelKey: 'layout.language.english',
        language: 'en',
    },
] as const satisfies readonly {
    label: string;
    labelKey: string;
    language: DashboardLanguage;
}[];

const isDashboardLanguage = (
    value: string | null | undefined,
): value is DashboardLanguage => {
    return dashboardLanguages.includes(value as DashboardLanguage);
};

const getStoredLanguage = (): DashboardLanguage => {
    if (typeof window === 'undefined') {
        return 'en';
    }

    const storedLanguage = window.localStorage.getItem(languageStorageKey);

    return isDashboardLanguage(storedLanguage) ? storedLanguage : 'en';
};

const applyDocumentLanguage = (language: DashboardLanguage) => {
    document.documentElement.lang = language;
};

const useDashboardLanguage = () => {
    const { i18n } = useTranslation();
    const [language, setLanguageState] =
        React.useState<DashboardLanguage>(getStoredLanguage);

    React.useEffect(() => {
        applyDocumentLanguage(language);
        i18n.changeLanguage(language);
        window.localStorage.setItem(languageStorageKey, language);
    }, [i18n, language]);

    React.useEffect(() => {
        const handleLanguageChange = (event: Event) => {
            const nextLanguage =
                event instanceof CustomEvent &&
                isDashboardLanguage(event.detail?.language)
                    ? event.detail.language
                    : getStoredLanguage();

            setLanguageState(nextLanguage);
        };

        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === languageStorageKey) {
                setLanguageState(getStoredLanguage());
            }
        };

        window.addEventListener(languageChangeEvent, handleLanguageChange);
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener(
                languageChangeEvent,
                handleLanguageChange,
            );
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    const setLanguage = React.useCallback((nextLanguage: DashboardLanguage) => {
        setLanguageState(nextLanguage);
        window.dispatchEvent(
            new CustomEvent(languageChangeEvent, {
                detail: {
                    language: nextLanguage,
                },
            }),
        );
    }, []);

    return [language, setLanguage] as const;
};

export const DashboardLanguageToggle: React.FC<
    DashboardLanguageToggleProps
> = ({ className, id, itemIdPrefix }) => {
    const { t } = useTranslation('components');
    const [language, setLanguage] = useDashboardLanguage();
    const languageToggleRootProps = {
        'data-testid': id,
        id,
    };

    const items = React.useMemo(
        () =>
            languageItems.map((languageItem) => ({
                ...languageItem,
                id: `${itemIdPrefix}-language-${languageItem.language}-button`,
            })),
        [itemIdPrefix],
    );

    return (
        <Segment
            {...languageToggleRootProps}
            className={className}
            onSelectionChange={(key) => {
                const nextLanguageItem = items.find(
                    (languageItem) => languageItem.id === String(key),
                );

                if (nextLanguageItem) {
                    setLanguage(nextLanguageItem.language);
                }
            }}
            selectedKey={
                items.find((languageItem) => languageItem.language === language)
                    ?.id
            }
            size="sm"
        >
            {items.map((languageItem) => (
                <Segment.Item
                    aria-label={t(languageItem.labelKey)}
                    className="h-7 px-2 text-xs font-semibold"
                    data-testid={languageItem.id}
                    id={languageItem.id}
                    key={languageItem.id}
                >
                    {languageItem.label}
                </Segment.Item>
            ))}
        </Segment>
    );
};
